import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Minus, ChevronDown, Table2 } from 'lucide-react';

const ALL_COLS = [
  { id: 'script', label: 'Script', step: 1, ok: s => !!(s.description || s.scene_prompt || s.start_action) },
  { id: 'cast', label: 'Cast', step: 0, ok: s => (s.character_ids || []).length > 0 },
  { id: 'location', label: 'Location', step: 1, ok: s => !!(s.location_id || s.location_ref_path) },
  { id: 'frames', label: 'Frames', step: 2, ok: s => !!(s.first_frame_path && s.last_frame_path), partial: s => !!(s.first_frame_path || s.last_frame_path) },
  { id: 'audio', label: 'Audio', step: 3, ok: s => { const l = (s.dialogue_lines || []).filter(x => x.text?.trim()); return l.length > 0 && l.every(x => x.character_id); }, partial: s => (s.dialogue_lines || []).some(x => x.text?.trim()) },
  { id: 'video', label: 'Video', step: 4, ok: s => !!s.video_path },
  { id: 'approved', label: 'Approved', step: 4, ok: s => !!s.video_approved },
];

// One row per scene, one column per production stage; click a cell to jump to that step
export function SceneMatrix({ projectId, scenes, simple = false }) {
  const COLS = simple ? ALL_COLS.filter(c => !['cast', 'location'].includes(c.id)) : ALL_COLS;
  const navigate = useNavigate();
  const [open, setOpen] = useState(scenes.length <= 12);
  const totals = COLS.map(c => scenes.filter(c.ok).length);
  return (
    <div data-testid="scene-matrix" className="ff-card overflow-hidden">
      <button data-testid="scene-matrix-toggle" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02]">
        <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8A8A93] flex items-center gap-2"><Table2 className="w-3.5 h-3.5 text-[#FF4500]" /> Progress overview</span>
        <span className="flex items-center gap-3 text-[11px] text-[#8A8A93]">
          <span data-testid="scene-matrix-approved-count">{totals[6]}/{scenes.length} approved</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-t border-white/[0.06] text-[#55555C]">
                <th className="text-left font-medium px-4 py-2">Scene</th>
                {COLS.map((c, i) => <th key={c.id} className="font-medium px-2 py-2 text-center whitespace-nowrap">{c.label}<span className="block text-[9px] text-[#3a3a40]">{totals[i]}/{scenes.length}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {scenes.map((s, i) => (
                <tr key={s.id} data-testid={`matrix-row-${s.id}`} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-4 py-1.5 text-white truncate max-w-[220px]"><span className="text-[#FF4500] font-bold mr-1.5">{i + 1}</span>{s.title}</td>
                  {COLS.map(c => {
                    const ok = c.ok(s); const partial = !ok && c.partial?.(s);
                    return (
                      <td key={c.id} className="px-2 py-1.5 text-center">
                        <button data-testid={`matrix-cell-${s.id}-${c.id}`} data-state={ok ? 'ok' : partial ? 'partial' : 'missing'} title={`${c.label}: ${ok ? 'done' : partial ? 'in progress' : 'missing'} — open`}
                          onClick={() => navigate(`/project/${projectId}/scenes/${s.id}?step=${c.step}`)}
                          className={`w-6 h-6 rounded-md inline-flex items-center justify-center transition-colors ${ok ? 'bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/30' : partial ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/30' : 'bg-white/[0.03] text-[#3a3a40] hover:bg-white/[0.08] hover:text-white'}`}>
                          {ok ? <Check className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
